// apps/widget/src/screens/capture.screen.tsx
//
// The hardest screen in the product, and the one that decides whether the
// numbers are worth anything. The camera, the live quality gate and the
// countdown live in <Viewfinder>, shared with the phone; this screen owns what
// happens to the photo on the desktop.

import { downscale, uploadPhoto } from '../lib/upload';
import { api } from '../lib/api';
import { detect } from '../lib/facemesh';
import { t } from '../lib/i18n';
import { send } from '../lib/postmessage';
import { useScan } from '../state';
import { Viewfinder, type Shot } from '../components/viewfinder';

export function CaptureScreen() {
  const { state, dispatch, go } = useScan();

  const upload = async ({ canvas, geometry, setProgress, stopCamera }: Shot): Promise<void> => {
    try {
      send('gs:scan:started');
      const blob = await downscale(canvas);

      // Kept in this tab so the report can show the face back with each score's
      // area marked. Detected again on the captured canvas itself: the live
      // `geometry` is from when the countdown began, three seconds earlier, and
      // any movement since put the marks off the face — on the background, on
      // clothing. No face in the photo, no marks: a stale position is worse
      // than none.
      const pinned = detect(canvas, performance.now());
      if (pinned) {
        dispatch({
          type: 'captured',
          capture: {
            photoUrl: URL.createObjectURL(blob),
            width: canvas.width,
            height: canvas.height,
            landmarks: pinned.landmarks,
          },
        });
      }

      const session =
        state.session ?? (await api.createSession(state.intake.declaredAge, { retainPhoto: state.retainPhoto }));
      if (!state.session) dispatch({ type: 'session', session });

      const { imageKey } = await uploadPhoto(session.sessionId, blob, setProgress);

      const { scanId } = await api.analyze(session.sessionId, {
        imageKey,
        ...(state.intake.declaredAge !== undefined ? { declaredAge: state.intake.declaredAge } : {}),
        // The crop box travels with the upload so the server does not repeat
        // face detection it cannot do as well.
        ...(geometry ? { faceBox: geometry.box } : {}),
        // Face geometry is biometric: it leaves this tab only when the visitor
        // agreed to keep their photo, so the clinic can see the marked regions.
        ...(state.retainPhoto && pinned
          ? { face: { width: canvas.width, height: canvas.height, landmarks: pinned.landmarks } }
          : {}),
      });

      stopCamera();
      dispatch({ type: 'scanning', scanId });
    } catch (e) {
      dispatch({
        type: 'error',
        error: {
          code: 'UPLOAD_FAILED',
          message: e instanceof Error ? e.message : 'Could not send that photo.',
        },
      });
    }
  };

  return (
    <Viewfinder
      onCapture={upload}
      // Offered always, not only on camera failure — phone cameras are better.
      extraAction={(busy) => (
        <button className="gs-btn gs-btn--ghost" onClick={() => go('qr')} disabled={busy}>
          {t('capture.usePhone')}
        </button>
      )}
      renderCameraError={(cameraError) => (
        <div className="gs-stack">
          <h1 className="gs-title">{t('error.camera')}</h1>
          <p className="gs-body">{cameraError.message}</p>
          {cameraError.suggestHandoff && (
            <button className="gs-btn gs-btn--primary" onClick={() => go('qr')}>
              {t('capture.usePhone')}
            </button>
          )}
        </div>
      )}
    />
  );
}
